import React, { useEffect, useId, useState } from 'react';
import { Dialog } from '../../common';
import type { LogVerificationRequest } from '../../../dtos/patientmr/insurance.dto';

interface LogVerificationDialogProps {
  open: boolean;
  /** Agency-local today ('YYYY-MM-DD'): default and maximum verification date */
  today: string;
  payerLabel: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: (entry: LogVerificationRequest) => void | Promise<void>;
  onCancel: () => void;
}

const EMPTY = (today: string): LogVerificationRequest => ({
  verifiedDate: today,
  method: 'PORTAL',
  pdnCovered: 'Y',
  priorAuthRequired: 'Y',
});

const inputClass = 'mt-1 w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';

/**
 * Log a manual eligibility verification (REQ-INS-10). Pelora never checks eligibility itself
 * (REQ-INS-09): the coordinator verifies in the TMHP or payer portal, by phone or fax, and records it.
 * Entries are append-only.
 */
const LogVerificationDialog: React.FC<LogVerificationDialogProps> = ({ open, today, payerLabel, busy = false, error, onConfirm, onCancel }) => {
  const id = useId();
  const [entry, setEntry] = useState<LogVerificationRequest>(EMPTY(today));

  useEffect(() => {
    if (open) setEntry(EMPTY(today));
  }, [open, today]);

  const set = <K extends keyof LogVerificationRequest>(key: K, value: LogVerificationRequest[K]) =>
    setEntry(prev => ({ ...prev, [key]: value }));

  const dateInvalid = !entry.verifiedDate || entry.verifiedDate > today;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (dateInvalid || busy) return;
    void onConfirm(entry);
  };

  const yesNo = (key: 'pdnCovered' | 'priorAuthRequired', label: string) => (
    <fieldset>
      <legend className="text-sm font-medium text-slate-700">{label} <span className="text-red-600" aria-hidden="true">*</span></legend>
      <div className="mt-1 flex gap-4">
        {(['Y', 'N'] as const).map(v => (
          <label key={v} className="inline-flex items-center gap-1 text-sm">
            <input type="radio" name={`${id}-${key}`} checked={entry[key] === v} onChange={() => set(key, v)} disabled={busy} />
            {v === 'Y' ? 'Yes' : 'No'}
          </label>
        ))}
      </div>
    </fieldset>
  );

  return (
    <Dialog open={open} onClose={() => !busy && onCancel()} labelledBy={`${id}-title`}>
      <form onSubmit={handleSubmit} className="text-left space-y-3">
        <h3 id={`${id}-title`} className="text-lg font-semibold text-slate-800 pr-6">Log verification</h3>
        <p className="text-sm text-slate-600">
          {payerLabel}. Verify eligibility in the TMHP or payer portal (or by phone or fax), then record what you found.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-medium text-slate-700">
            Date verified <span className="text-red-600" aria-hidden="true">*</span>
            <input type="date" className={inputClass} value={entry.verifiedDate} max={today} required
              onChange={e => set('verifiedDate', e.target.value)} disabled={busy} aria-invalid={dateInvalid} />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Method <span className="text-red-600" aria-hidden="true">*</span>
            <select className={inputClass} value={entry.method} onChange={e => set('method', e.target.value as LogVerificationRequest['method'])} disabled={busy}>
              <option value="PORTAL">Portal</option>
              <option value="PHONE">Phone</option>
              <option value="FAX">Fax</option>
            </select>
          </label>
          <label className="text-sm font-medium text-slate-700">
            Person spoken to
            <input className={inputClass} value={entry.contactName || ''} maxLength={120} onChange={e => set('contactName', e.target.value)} disabled={busy} />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Reference #
            <input className={inputClass} value={entry.referenceNumber || ''} maxLength={60} onChange={e => set('referenceNumber', e.target.value)} disabled={busy} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {yesNo('pdnCovered', 'PDN covered')}
          {yesNo('priorAuthRequired', 'Prior auth required')}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <label className="text-sm font-medium text-slate-700 col-span-3">
            Hour / visit limit
            <input className={inputClass} value={entry.hourOrVisitLimit || ''} maxLength={120} placeholder="e.g. 40 hours/week" onChange={e => set('hourOrVisitLimit', e.target.value)} disabled={busy} />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Deductible
            <input className={inputClass} value={entry.deductible || ''} maxLength={60} onChange={e => set('deductible', e.target.value)} disabled={busy} />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Copay
            <input className={inputClass} value={entry.copay || ''} maxLength={60} onChange={e => set('copay', e.target.value)} disabled={busy} />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-medium text-slate-700">
            Eligible from
            <input type="date" className={inputClass} value={entry.eligibilityStart || ''} onChange={e => set('eligibilityStart', e.target.value)} disabled={busy} />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Eligible to
            <input type="date" className={inputClass} value={entry.eligibilityEnd || ''} onChange={e => set('eligibilityEnd', e.target.value)} disabled={busy} />
          </label>
        </div>
        <label className="block text-sm font-medium text-slate-700">
          Note
          <textarea className={inputClass} rows={2} maxLength={500} value={entry.note || ''} onChange={e => set('note', e.target.value)} disabled={busy} />
        </label>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onCancel} disabled={busy}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-60">
            Cancel
          </button>
          <button type="submit" disabled={dateInvalid || busy}
            className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? 'Saving...' : 'Save verification'}
          </button>
        </div>
      </form>
    </Dialog>
  );
};

export default LogVerificationDialog;
